import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router";
import { Card, CardBody, CardHeader } from "reactstrap";
import { Typography, Tooltip } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";

import ALoader from "views/pages/manage/common/a-loader";
import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import { ReactComponent as DeleteIcon } from "assets/img/svg/Delete.svg";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ATextAreaV2 from "components/ATextAreaV2/ATextAreaV2";
import SelectSearchInputParam from "views/pages/manage/edpOps/studentSerach/SelectSearchInputParam";
import { formattedPrice } from "views/pages/manage/unAssigned/collectPayment/utils";

import { failureToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import {
  getVendorDetails,
  searchStudentsForPsidMapping,
  createVendorPsidMapping,
  capitalize,
  getValidatedPasteHandler,
} from "./helper";
import { setBuAndTerm } from "redux/globalData/globalDataAction";
import { getInputParamRegex } from "../../edpOps/constant/student-search";
import PsidMappingSuccessDialog from "./PsidMappingSuccessDialog";

const VendorPsidMapping = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [vendorData, setVendorData] = useState(null);
  const [error, setError] = useState(null);

  const [searchInputParam, setSearchInputParam] = useState("psid");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);

  const [searchLoading, setSearchLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const masterDropdowns = useSelector(
    (state) => state.globalData?.masterDropdowns,
  );

  useEffect(() => {
    fetchVendorData();
    dispatch(setBuAndTerm());
    // fetchMappedStudents();
  }, [id]);

  const fetchVendorData = async () => {
    try {
      const response = await getVendorDetails(id);
      setError(true);
      if (response?.vendorSapCode) {
        setVendorData(response);
        setError(null);
      }
    } catch (error) {
      console.error("Error fetching vendor data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudents = async () => {
    if (!searchInput.trim()) return;

    // Validate input based on searchInputParam
    const regex = getInputParamRegex(searchInputParam);
    const inputs = searchInput
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);

    const invalidInputs = inputs.filter((input) => !regex.test(input));
    if (invalidInputs.length > 0) {
      failureToast(
        `Invalid ${searchInputParam === "psid" ? "PSID" : "Application ID"}(s): ${invalidInputs.join(", ")}`,
      );
      return;
    }

    setSearchLoading(true);
    try {
      const payload = {
        searchBy: searchInputParam,
        searchByValues: inputs,
      };

      const response = await searchStudentsForPsidMapping(payload);

      const foundStudents =
        response?.data.studentApplicationDetailsSearchResponseDTOList || [];

      const uniqueAppIds = selectedStudents.map((item) => item.applicationId);
      const newStudents = foundStudents.filter(
        (obj) => !uniqueAppIds.includes(obj.applicationId),
      );

      setSelectedStudents([...selectedStudents, ...newStudents]);
      setSearchInput("");
    } catch (error) {
      console.error("Error searching students:", error);
      failureToast("Error searching students");
    } finally {
      setSearchLoading(false);
    }
  };

  // Save handler: mark row as saved and show Edit icon
  const handleSaveStudent = (row) => {
    setSelectedStudents((prev) =>
      prev.map((student) =>
        student.applicationId === row.applicationId
          ? { ...student, isSaved: true, isEditing: false }
          : student,
      ),
    );
  };

  const handleDeleteStudent = (row) => {
    const index = selectedStudents.findIndex(
      (student) => student.applicationId === row.applicationId,
    );
    if (index !== -1) {
      const newSelected = [...selectedStudents];
      newSelected.splice(index, 1);
      setSelectedStudents(newSelected);
    }
  };

  // const handleProceedNext = () => {
  //   setStep("confirm");
  // };

  const handleSendRequest = async () => {
    try {
      // Prepare payload with selected students
      const payload = {
        vendorId: id,
        applicationDetails: selectedStudents,
      };

      const response = await createVendorPsidMapping(payload);

      if (response) {
        setShowSuccessDialog(true);
      }
    } catch (error) {
      console.error("Error creating PSID mapping:", error);
      failureToast("Error creating PSID mapping");
    }
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      window.close(); // will close only if tab was opened by script
    }
  };

  if (loading) {
    return <ALoader />;
  }

  const allStudents = [...selectedStudents];

  const columns =
    // useMemo( () =>
    [
      new AGridColDef("psid", "PSID")
        .setMinWidth(120)
        .renderCellComponent((params) => (
          <strong
            className="color-primary cursor"
            onClick={() =>
              window.open(
                history.createHref({
                  pathname: `admin/edp/studentDetails/${params?.value}`,
                }),
                "_blank",
              )
            }
          >
            {params.value}
          </strong>
        )),
      new AGridColDef("applicationId", "Application ID")
        .setMinWidth(120)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("studentName", "Student Name")
        .setMinWidth(150)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("programAction", "Program Action")
        .setMinWidth(120)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("admissionSource", "Admission Source")
        .setMinWidth(130)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("totalRealisedAmount", "Total Realised Fees")
        .setMinWidth(140)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={formattedPrice(params.value ?? 0)}>
              <span>{formattedPrice(params.value ?? 0)}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("term", "Term")
        .setMinWidth(80)
        .renderCellComponent((params) => {
          const term = masterDropdowns?.term?.find(
            (t) => t.id === params.value,
          );
          const displayValue = term ? term.label : params.value || "-";
          return (
            <Tooltip title={displayValue}>
              <span>{displayValue}</span>
            </Tooltip>
          );
        }),
      new AGridColDef("businessArea", "BU")
        .setMinWidth(80)
        .renderCellComponent((params) => {
          const bu = masterDropdowns?.businessArea?.find(
            (b) => b.businessAreaId === params.value,
          );
          const displayValue = bu ? bu.label : params.value || "-";
          return (
            <Tooltip title={displayValue}>
              <span>{displayValue}</span>
            </Tooltip>
          );
        }),
      new AGridColDef("academicGroupDispValue", "Stream")
        .setMinWidth(100)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("academicCareerValue", "Career")
        .setMinWidth(100)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),
      new AGridColDef("courseId", "Course Id")
        .setMinWidth(100)
        .renderCellComponent((params) =>
          params.value ? (
            <Tooltip title={params.value}>
              <span>{params.value}</span>
            </Tooltip>
          ) : (
            params.value || "-"
          ),
        ),

      new AGridColDef("isEligibleForVendorMapping", "Actions", false)
        .setMinWidth(120)
        .pinColumnLast(true)
        .renderCellComponent((params) => {
          const {
            isEligibleForVendorMapping,
            vendorMappingIneligibilityReason,
            isSaved,
          } = params.row;
          // Show Edit icon if saved
          if (isSaved) {
            return (
              <Tooltip title="Edit mapping" placement="top" arrow>
                <span>
                  <AButton
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      setSelectedStudents((prev) =>
                        prev.map((student) =>
                          student.applicationId === params.row.applicationId
                            ? { ...student, isEditing: true, isSaved: false }
                            : student,
                        ),
                      );
                    }}
                  >
                    Edit
                  </AButton>
                </span>
              </Tooltip>
            );
          }
          // Default: show Save and Delete
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Tooltip
                title={
                  isEligibleForVendorMapping
                    ? "Save mapping for this student"
                    : vendorMappingIneligibilityReason ||
                      "Not eligible for mapping"
                }
                placement="top"
                arrow
              >
                <span>
                  <AButton
                    size="xs"
                    variant="primary_filled"
                    disabled={!isEligibleForVendorMapping}
                    onClick={() => handleSaveStudent(params.row)}
                    className="no-ml-btn"
                  >
                    Save
                  </AButton>
                </span>
              </Tooltip>
              <button
                onClick={() => handleDeleteStudent(params.row)}
                className="btn btn-sm"
                title="Delete"
              >
                <DeleteIcon />
              </button>
            </div>
          );
        }),
    ];
  //   [step, masterDropdowns], );

  if (error) {
    return (
      <div className="p-4">
        <div className="d-flex align-items-center mb-3">
          <BackIcon style={{ cursor: "pointer" }} onClick={handleBack} />
          <h3 className="heading-3 mb-0 ml-2">
            {" "}
            Admission Advisor- PSID Mapping
          </h3>
        </div>
        <Card className="p-5 text-center">
          <div style={{ padding: "40px 20px" }}>
            <h4 className="text-muted mb-3">Vendor Details Not Found</h4>
            <p className="text-secondary">
              {error || "The requested payment slab could not be found."}
            </p>
            <button className="btn btn-primary mt-3" onClick={handleBack}>
              Go Back
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="large-text mx-4 mt-2 mb-3 pb-4 label-color"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <IconButtonWrapper onClick={handleBack}>
            <BackIcon />
          </IconButtonWrapper>
          Admission Advisor- PSID Mapping
        </div>
        <Card className="p-2 text-sm flex-row align-items-center">
          <div style={{ color: "#AFAFAF" }}>
            {" "}
            {capitalize(vendorData?.vendorType) || "-"} &nbsp;
          </div>

          <span className="text-bold">{vendorData?.vendorSapCode}</span>
        </Card>
      </div>

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          paddingBottom: allStudents.length > 0 ? "80px" : "0",
        }}
      >
        <Card>
          <CardHeader>
            <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
              Search and add students
            </Typography>
          </CardHeader>
          <CardBody>
            {/* {step === "select" ? ( */}
            <div className="d-flex gap-1">
              <div className="mr-3">
                <ATextAreaV2
                  label="Enter PSIDs or Application IDs (comma separated)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="e.g., PSID1, PSID2 or APP1, APP2"
                  onPaste={getValidatedPasteHandler(
                    searchInputParam,
                    setSearchInput,
                  )}
                  style={{ height: "42px" }}
                />
                <div className="mt-5">
                  <SelectSearchInputParam
                    searchInputParam={searchInputParam}
                    setSearchInputParam={setSearchInputParam}
                    isHideName={true}
                  />
                </div>
              </div>
              <div className="ml-4 d-flex">
                <AButton
                  variant="primary_filled"
                  onClick={handleAddStudents}
                  disabled={searchLoading || !searchInput.trim()}
                >
                  {searchLoading ? "Searching..." : "Add Students"}
                </AButton>
              </div>
            </div>
            {/* ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "15px",
                  fontSize: "14px",
                }}
              >
                <div>
                  <span style={{ color: "#757575" }}>Vendor Type:</span>
                  <br />
                  <span className="text-bold">
                    {capitalize(vendorData?.vendorType) || "-"}
                  </span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>Vendor Name:</span>
                  <br />
                  <span className="text-bold">
                    {vendorData?.vendorFirstName || ""}{" "}
                    {vendorData?.vendorLastName || ""}
                  </span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>PAN Card Number:</span>
                  <br />
                  <span className="text-bold">
                    {vendorData?.panNumber || "-"}
                  </span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>Transaction Number:</span>
                  <br />
                  <span className="text-bold">-</span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>Vendor ID:</span>
                  <br />
                  <span className="text-bold">{vendorData?.id || "-"}</span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>Approval Status:</span>
                  <br />
                  <span className="text-bold">{vendorData?.status || "-"}</span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>SAP Status:</span>
                  <br />
                  <span className="text-bold">-</span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>BU:</span>
                  <br />
                  <span className="text-bold">
                    {vendorData?.businessAreas?.[0]?.businessAreaId || "-"}
                  </span>
                </div>
                <div>
                  <span style={{ color: "#757575" }}>Year:</span>
                  <br />
                  <span className="text-bold">-</span>
                </div>
              </div>
            )} */}
          </CardBody>
        </Card>

        <Card className="mt-4">
          <CardHeader>Students mapped</CardHeader>
          <CardBody className="pt-0">
            <ADataGrid
              rows={allStudents}
              rowCount={allStudents.length}
              columns={columns}
              rowId={(row) => row.applicationId}
              loading={searchLoading}
              height="400px"
              pageSize={50}
              pagination
              pinLastColumn
            />
          </CardBody>
        </Card>
      </div>

      {/* Sticky Footer */}
      <div
        style={{
          boxShadow: "0px -6px 4px rgba(0, 0, 0, 0.08)",
          position: "fixed",
          bottom: 0,
          width: "calc(100% - 59px)",
          margin: "0 -2.5rem",
          padding: "1rem 1.25rem",
          height: "80px",
          backgroundColor: allStudents.length === 0 ? "#757575" : "#004662",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <div className="d-flex justify-content-between align-items-center w-100 px-4">
          <div className="heading-4">Map student PSIDs</div>
          <div className="d-flex align-items-center">
            <AButton
              variant="primary_filled"
              onClick={handleSendRequest}
              disabled={
                allStudents.length === 0 ||
                allStudents.some((row) => !row.isEligibleForVendorMapping) ||
                allStudents.some(
                  (row) => row.isEligibleForVendorMapping && !row.isSaved,
                )
              }
            >
              Proceed Next
            </AButton>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      <PsidMappingSuccessDialog
        open={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          setSearchInput("");
          setSelectedStudents([]);
        }}
        vendorData={vendorData}
        selectedStudentsCount={selectedStudents.length}
      />
    </div>
  );
};

export default VendorPsidMapping;
